/**
 * One step's parameters in Customize mode (E3, spec 2026-09-18 §6 / §3.1).
 * Every input reads overlay-over-base and reports a definition-shaped path
 * (`['executor', 'seat']`); the store prunes a value equal to the base. Only
 * what the step's executor type accepts is offered — the same table the
 * server enforces (`overlay_step_field_not_for_executor`). The contract
 * renders as locked rows in `overlay` mode.
 *
 * `document` mode (custom rows, spec §6 Edit) is the same card over the
 * author's draft: `base` is the draft itself, `overlay` is empty, `onParam`
 * writes straight into the document, and the contract is editable through
 * `onDocumentChange` instead of locked.
 *
 * SA-1060 (spec 2026-10-09 §2.6): the card is closed by default with a
 * summary line; the first screen inside is title, how it runs, seat and
 * due; the AI block and the contract rows sit behind two closed disclosures.
 */
import { useRef, useState, type ReactNode } from 'react'
import { Input, NativeSelect, Switch, Textarea } from '@/components/ui'
import type { ProcessStepVM } from '@/utils/workflowOverview'
import type { AiExecutorJson, ProcessStepJson, ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { WorkflowOverlayJson } from '@/types/workflowConfig'
import { LockedRow } from './LockedRow'
import { ProcessStepContractEditor } from './ProcessStepContractEditor'
import { EditorDisclosure, ProcessEditorCard } from './ProcessEditorCard'
import { aiCard, contractCard, stepCard, type EditorCards } from './editorCards'
import { stepSummary } from './editorSummaries'

type Path = string[]

const MESSAGE_ROWS: { key: 'attention' | 'wait' | 'guidance'; label: string; hint: string }[] = [
  { key: 'attention', label: 'Attention-task title', hint: 'Needs a person: {title}' },
  { key: 'wait', label: 'Wait-task title', hint: 'Record: {title}' },
  { key: 'guidance', label: 'Guidance', hint: 'Shown above the task form' },
]

/** A number box that can be emptied and retyped: the text is local while
 * focused, and only a finite number is committed. A controlled number input
 * that ignores '' gets its old value written straight back by React. */
function ThresholdInput({ value, onCommit }: { value: number; onCommit: (n: number) => void }) {
  const [text, setText] = useState<string | null>(null)
  return (
    <Input
      aria-label="Confidence threshold"
      type="number"
      min={0.01}
      max={1}
      step={0.01}
      value={text ?? value}
      onChange={(e) => {
        setText(e.target.value)
        const n = Number(e.target.value)
        if (e.target.value !== '' && Number.isFinite(n)) onCommit(n)
      }}
      onBlur={() => setText(null)}
    />
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1 text-xs font-medium text-foreground">
      <span>{label}</span>
      {children}
    </label>
  )
}

export function ProcessStepEditor({
  step,
  base,
  overlay,
  mode,
  onParam,
  onDocumentChange,
  actions,
  children,
  cards,
  edited = false,
  contract,
}: {
  step: ProcessStepVM
  base: ProcessWorkflowDefinitionJson
  overlay: WorkflowOverlayJson
  /** `overlay` = a platform row: parameters editable, contract locked. */
  mode: 'overlay' | 'document'
  onParam: (stepId: string, path: Path, value: unknown) => void
  /** `document` mode only: a whole-key patch on the step's contract. */
  onDocumentChange?: (stepId: string, patch: Partial<ProcessStepJson>) => void
  /** Rendered beside the heading (document mode's move / remove controls). */
  actions?: ReactNode
  /** Rendered at the foot of the card, outside every disclosure (document mode's remove refusal). */
  children?: ReactNode
  /** SA-1060: the open state of this card and its two disclosures. */
  cards: EditorCards
  /** SA-1060: the card carries the edited mark (the parent decides, ruling Q3). */
  edited?: boolean
  /** Document mode's exit editor, rendered inside "Contract and wording". */
  contract?: ReactNode
}) {
  const raw = base.steps[step.id]
  const executor = raw.executor
  const languages = base.languages?.length ? base.languages : ['en']
  const lang = (label: string, l: string) => (l === languages[0] ? label : `${label} (${l})`)
  const read = <T,>(path: Path, fallback: T): T => {
    let node: unknown = overlay.steps?.[step.id]
    for (const seg of path) {
      if (!node || typeof node !== 'object') return fallback
      node = (node as Record<string, unknown>)[seg]
    }
    return (node ?? fallback) as T
  }
  const set = (path: Path, value: unknown) => onParam(step.id, path, value)

  const hasSeat = executor.type === 'person' || executor.type === 'ai' || executor.type === 'wait'
  const hasForm = executor.type === 'person' || executor.type === 'ai'
  const ai: AiExecutorJson | null = executor.type === 'ai' ? executor : null
  const baseSeat = hasSeat ? (executor.seat ?? '') : ''
  const baseForm = hasForm ? executor.form : null
  const produces = raw.produces ?? []
  // Candidates in a stable order, so re-ticking every box IS the base list.
  const formCandidates = [...produces, ...(baseForm ?? []).filter((f) => !produces.includes(f))]
  const form = read<string[]>(['executor', 'form'], baseForm ?? produces)
  // `timeout.after` is tunable only where the base arms it with a duration;
  // a field-armed deadline has none (spec D20 §7.3) and reads as contract.
  // In document mode the contract editor owns the whole timeout block.
  const durationTimeout = mode === 'overlay' && raw.timeout?.after ? raw.timeout : null
  const dueAfter = read(['due', 'after'], raw.due?.after ?? '')
  // Document mode: an emptied "Due after" IS no due, so the block (calendar
  // included) leaves the draft. Retyping it must not quietly turn a
  // business-day deadline into a clock one — the card remembers.
  const lastDueCalendar = useRef(raw.due?.calendar)
  if (raw.due?.calendar) lastDueCalendar.current = raw.due.calendar
  const seatValue = read(['executor', 'seat'], baseSeat)
  const id = stepCard(step.id)

  return (
    <ProcessEditorCard
      cardId={id}
      title={`${step.number} ${step.title}`}
      label={`Step ${step.number}: ${step.title}`}
      summary={stepSummary(base, overlay, step.id)}
      edited={edited}
      open={cards.isOpen(id)}
      onOpenChange={(open) => cards.setOpen(id, open)}
      actions={actions}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {languages.map((l) => (
          <Row key={l} label={lang('Title', l)}>
            <Input
              aria-label={lang('Title', l)}
              value={read(['title', l], raw.title?.[l] ?? '')}
              onChange={(e) => set(['title', l], e.target.value)}
            />
          </Row>
        ))}
        {raw.level && (
          <Row label="How it runs">
            <NativeSelect
              aria-label="How it runs"
              value={read(['level', 'default'], raw.level.default)}
              onChange={(e) => set(['level', 'default'], e.target.value)}
            >
              {step.levelsOffered.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </NativeSelect>
          </Row>
        )}
        {hasSeat && (
          <Row label="Seat">
            <NativeSelect
              aria-label="Seat"
              value={seatValue}
              onChange={(e) => set(['executor', 'seat'], e.target.value)}
            >
              {!baseSeat && <option value="">— none —</option>}
              {/* A seat the document no longer declares still shows as itself. */}
              {seatValue && !(seatValue in base.seats) && <option value={seatValue}>{seatValue} (unknown)</option>}
              {Object.entries(base.seats).map(([id, seat]) => (
                <option key={id} value={id}>
                  {seat.label?.en ?? id}
                </option>
              ))}
            </NativeSelect>
          </Row>
        )}
        <Row label="Due after">
          <Input
            aria-label="Due after"
            placeholder="e.g. 4h, 1d"
            value={dueAfter}
            onChange={(e) => {
              set(['due', 'after'], e.target.value)
              // A due ADDED here lives whole in the overlay: without its
              // `after` the calendar alone merges to an invalid due.
              if (e.target.value === '' && !raw.due) set(['due', 'calendar'], '')
              if (mode === 'document' && e.target.value !== '' && !raw.due && lastDueCalendar.current) {
                set(['due', 'calendar'], lastDueCalendar.current)
              }
            }}
          />
        </Row>
        <Row label="Due calendar">
          <NativeSelect
            aria-label="Due calendar"
            disabled={dueAfter === ''}
            value={read(['due', 'calendar'], raw.due?.calendar ?? 'clock')}
            onChange={(e) => set(['due', 'calendar'], e.target.value)}
          >
            <option value="clock">clock</option>
            <option value="business">business</option>
          </NativeSelect>
        </Row>
        {durationTimeout && (
          <>
            <Row label={`Timeout after (exits “${durationTimeout.exit.replace(/_/g, ' ')}”)`}>
              <Input
                aria-label="Timeout after"
                value={read(['timeout', 'after'], durationTimeout.after ?? '')}
                onChange={(e) => set(['timeout', 'after'], e.target.value)}
              />
            </Row>
            <Row label="Timeout calendar">
              <NativeSelect
                aria-label="Timeout calendar"
                value={read(['timeout', 'calendar'], durationTimeout.calendar ?? 'clock')}
                onChange={(e) => set(['timeout', 'calendar'], e.target.value)}
              >
                <option value="clock">clock</option>
                <option value="business">business</option>
              </NativeSelect>
            </Row>
          </>
        )}
      </div>

      {hasForm && formCandidates.length > 0 && (
        <fieldset className="text-xs">
          <legend className="mb-1 font-medium text-foreground">Form fields</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {formCandidates.map((f) => (
              <label key={f} className="inline-flex items-center gap-1.5 text-foreground">
                <input
                  type="checkbox"
                  checked={form.includes(f)}
                  onChange={(e) =>
                    set(
                      ['executor', 'form'],
                      formCandidates.filter((c) => (c === f ? e.target.checked : form.includes(c))),
                    )
                  }
                />
                {base.fields[f]?.label?.en ?? f}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer font-medium text-foreground">Task wording</summary>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {MESSAGE_ROWS.filter((m) => m.key !== 'wait' || executor.type === 'wait').flatMap((m) =>
            languages.map((l) => (
              <Row key={`${m.key}:${l}`} label={lang(m.label, l)}>
                <Input
                  aria-label={lang(m.label, l)}
                  placeholder={m.hint}
                  value={read(['messages', m.key, l], raw.messages?.[m.key]?.[l] ?? '')}
                  onChange={(e) => set(['messages', m.key, l], e.target.value)}
                />
              </Row>
            )),
          )}
        </div>
      </details>

      {ai && (
        <EditorDisclosure
          title="AI instruction and threshold"
          open={cards.isOpen(aiCard(step.id))}
          onOpenChange={(open) => cards.setOpen(aiCard(step.id), open)}
        >
          {languages.map((l) => (
            <Row key={l} label={lang('Instruction to the AI', l)}>
              <Textarea
                aria-label={lang('Instruction to the AI', l)}
                rows={4}
                // `?.`: a draft's cleared instruction is a missing key (and a
                // server finding), never a reason to lose the form.
                value={read(['executor', 'instruction', l], ai.instruction?.[l] ?? '')}
                onChange={(e) => set(['executor', 'instruction', l], e.target.value)}
              />
            </Row>
          ))}
          <div className="grid gap-3 sm:grid-cols-2">
            <Row label="Confidence needed to run automatically">
              <ThresholdInput
                value={read(['executor', 'threshold'], ai.threshold)}
                onCommit={(n) => set(['executor', 'threshold'], n)}
              />
            </Row>
            <div className="flex items-center gap-2 text-xs font-medium text-foreground">
              <Switch
                size="sm"
                aria-label="Reads attachments"
                checked={read(['executor', 'attachments'], ai.attachments)}
                onCheckedChange={(checked) => set(['executor', 'attachments'], checked)}
              />
              Reads attachments
            </div>
          </div>
        </EditorDisclosure>
      )}

      <EditorDisclosure
        title="Contract and wording"
        open={cards.isOpen(contractCard(step.id))}
        onOpenChange={(open) => cards.setOpen(contractCard(step.id), open)}
      >
        {mode === 'document' && onDocumentChange && (
          <ProcessStepContractEditor stepId={step.id} doc={base} onChange={onDocumentChange} />
        )}
        {contract}
        {mode === 'overlay' && (
          <div className="grid gap-2 sm:grid-cols-2">
            <LockedRow label="Needs" value={step.needs.map((n) => n.label).join(', ') || '—'} />
            <LockedRow label="Produces" value={step.produces.map((p) => p.label).join(', ') || '—'} />
            <LockedRow label="Next" value={step.exits.map((e) => `${e.name} → ${e.targetLabel}`).join(' · ') || '—'} />
            <LockedRow label="Who (executor type)" value={executor.type} />
            <LockedRow label="Lane" value={step.laneLabel} />
            {step.timeout?.fromFieldLabel && (
              <LockedRow
                label="Timeout"
                value={`when “${step.timeout.fromFieldLabel}” passes → ${step.timeout.exit.replace(/_/g, ' ')}`}
              />
            )}
          </div>
        )}
      </EditorDisclosure>
      {children}
    </ProcessEditorCard>
  )
}
